import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowRight, Plus, Pencil, Trash2, X, Quote, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const AdminQuotesPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ text: '', author: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user?.is_admin || user?.admin_role === 'محرر') { navigate('/admin'); return; }
    loadQuotes();
  }, []);

  const loadQuotes = async () => {
    try { const res = await api.get('/admin/quotes'); setQuotes(res.data); }
    catch { toast.error('فشل تحميل العبارات'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!form.text.trim()) { toast.error('يرجى كتابة العبارة'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/quotes/${editing.id}`, form);
        toast.success('تم تحديث العبارة');
      } else {
        await api.post('/admin/quotes', form);
        toast.success('تم إضافة العبارة');
      }
      setModalOpen(false);
      setEditing(null);
      setForm({ text: '', author: '' });
      loadQuotes();
    } catch { toast.error('فشل حفظ العبارة'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (q) => {
    if (!window.confirm('هل تريد حذف هذه العبارة؟')) return;
    try { await api.delete(`/admin/quotes/${q.id}`); toast.success('تم الحذف'); loadQuotes(); }
    catch { toast.error('فشل الحذف'); }
  };

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto"></div></div>;

  return (
    <div className="space-y-8" data-testid="admin-quotes-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/admin')}><ArrowRight className="w-5 h-5" /></Button>
          <div>
            <h1 className="text-4xl font-bold text-accent">العبارات التشجيعية</h1>
            <p className="text-muted-foreground text-lg mt-1">{quotes.length} عبارة</p>
          </div>
        </div>
        <Button onClick={() => { setEditing(null); setForm({ text: '', author: '' }); setModalOpen(true); }} className="bg-primary hover:bg-primary/90" data-testid="add-quote-btn">
          <Plus className="w-5 h-5 ml-2" /> إضافة عبارة
        </Button>
      </div>

      {quotes.length === 0 ? (
        <div className="text-center py-16">
          <Sparkles className="w-16 h-16 text-muted mx-auto mb-4 opacity-50" />
          <p className="text-lg text-muted-foreground">لا توجد عبارات بعد</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {quotes.map((q) => (
            <Card key={q.id} className="p-6 relative group hover:shadow-md transition-shadow" data-testid={`quote-${q.id}`}>
              <Quote className="w-8 h-8 text-primary/20 mb-3" />
              <p className="text-lg leading-relaxed text-accent mb-3 whitespace-pre-wrap">{q.text}</p>
              {q.author && <p className="text-sm text-muted-foreground">- {q.author}</p>}
              <div className="absolute top-4 left-4 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => { setEditing(q); setForm({ text: q.text, author: q.author || '' }); setModalOpen(true); }}><Pencil className="w-3.5 h-3.5" /></Button>
                <Button variant="outline" size="icon" className="h-8 w-8 text-red-500" onClick={() => handleDelete(q)}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-accent">{editing ? 'تعديل العبارة' : 'إضافة عبارة جديدة'}</h2>
              <button onClick={() => setModalOpen(false)}><X className="w-6 h-6 text-muted-foreground" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">العبارة *</label>
                <textarea value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} placeholder="اكتب العبارة التشجيعية هنا..." className="w-full border-2 rounded-xl p-3 min-h-[120px] resize-y focus:outline-none focus:border-primary" dir="rtl" data-testid="quote-text-input" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">المصدر / القائل</label>
                <Input value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} placeholder="اختياري..." data-testid="quote-author-input" />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button onClick={handleSave} disabled={saving} className="flex-1 bg-primary hover:bg-primary/90" data-testid="save-quote-btn">
                {saving ? 'جارٍ الحفظ...' : editing ? 'تحديث' : 'إضافة'}
              </Button>
              <Button variant="outline" onClick={() => setModalOpen(false)} className="flex-1">إلغاء</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminQuotesPage;
